import { type ColumnsType, type ColumnType } from 'antd/es/table';
import { FilterInputType } from '../../components/table/TableColumnFilter.tsx';
import { ColumnSortDirections } from '../../components/table/TableColumnSort.tsx';

type FilterMap = Record<string, string[]> | null;

export interface PaginationUrlQuery {
  // API con ordinamenti sotto forma di lista di stringhe
  ordinamenti?: string[];
  // API ad ordinamento singolo (old)
  sortBy?: string;
  sortDirection?: string; // ASC | DESC
  [key: string]: any;
}

/**
 * Imposta i filtri indicati dalla query nelle colonne della table.
 * @param columns colonne della table
 * @param query query in uso sui dati
 * @param filterMap mappatura dei campi della query rispetto ai filtri.
 * Per esempio la data di creazione (un filtro unico) contiene due valori (da-a); dovrà essere mappata come {createdAt: ['createdAtFrom', 'createdAtTo']}.
 * Oppure, se un dataIndex differisce dalla query, ex: { dataIndexLabelA: ['dataIndexLabelB']}
 * Se un campo mantiene la stessa chiave sia nella query che nel dataIndex, non è necessario indicarlo.
 */
function setFilters<T, Q extends PaginationUrlQuery>(
  columns: ColumnsType<T>,
  query: Q,
  filterMap?: FilterMap,
) {
  return columns.map((column: ColumnType<T>) => ({
    ...column,
    filteredValue: getFilterArray(column, query, filterMap),
  }));
}

/**
 * Se abbiamo es. `flagFrontoffice=true` come filtro ed è una checkbox,
 * dobbiamo restituire il valore come array a `['flagFrontoffice'] (con
 * il _nome_ del filtro, non il valore).
 */
function mapQueryParamToFilterValue(
  column: ColumnType<any>,
  filterId: string,
  param: string,
) {
  if (column.filterInputType === FilterInputType.CheckBox) {
    return param === 'true' ? [filterId] : [];
  }

  return [param];
}

/**
 * Ritorna l'array con i filtri indicati nella query
 */
function getFilterArray<T, Q extends PaginationUrlQuery>(
  column: ColumnType<T>,
  query: Q,
  filterMap?: FilterMap,
): string[] | null {
  // Se la colonna non è filtrabile o non presenta un dataIndex, non imposta il filtro.
  if (!column.filterDropdown) return null;
  if (!column.dataIndex) return null;
  const dataIndex = column.dataIndex;

  // Gestisco il caso in cui sia un array
  if (Array.isArray(dataIndex)) {
    return dataIndex
      .flatMap(dataIndexItem =>
        getFilterValue(dataIndexItem as string, column, query, filterMap),
      )
      .filter(Boolean);
  }
  return getFilterValue(dataIndex as string, column, query, filterMap);
}

/**
 * Estrae il valore del filtro relativo al dataIndex fornito
 */
function getFilterValue<T, Q extends PaginationUrlQuery>(
  dataIndex: string,
  column: ColumnType<T>,
  query: Q,
  filterMap: FilterMap | undefined,
) {
  // Mappa il filtro nell'array secondo la mappa
  if (filterMap?.[dataIndex]) {
    const filter = filterMap[dataIndex].map(filterId => query[filterId]);

    // Se tutti i risultati non sono definiti, ignora il filtro
    if (filter.every(f => f == null)) {
      return null;
    }
    return filterMap[dataIndex].flatMap(filterId =>
      mapQueryParamToFilterValue(column, filterId, query[filterId]),
    );
  }

  // Se non è indicato nella mappa,
  // recupera il valore del filtro direttamente dalla query
  return query[dataIndex] ? [query[dataIndex]] : null;
}

/**
 * Rimuove tutti gli ordinamenti presenti sulle colonne passate in input
 */
function resetSortOrder<T>(columns: ColumnsType<T>): ColumnsType<T> {
  return columns.map(column => ({ ...column, sortOrder: null }));
}

/**
 * Ritorna il field per cui si ordina la query.
 * Sul progetto le api utilizzano gli ordinamenti sotto forma di lista di stringhe,
 * dove se la stringa inizia con "-" indica un ordinamento decrescente.
 */
export function getOrderBy(query: PaginationUrlQuery | undefined) {
  // Controlliamo se è presente il campo sortBy (vecchio formato)
  if (query?.sortBy) {
    return query.sortBy;
  }

  // Se non è presente il campo ordinamenti, non c'è un ordinamento
  if (!query?.ordinamenti) {
    return null;
  }

  return query.ordinamenti[0]?.startsWith('-')
    ? query.ordinamenti[0]?.slice(1)
    : query.ordinamenti[0];
}

function getOrderDirection(
  query: PaginationUrlQuery,
): ColumnSortDirections | null {
  // Controlliamo se è presente il campo sortBy (vecchio formato)
  // (dove la direzione è ASC o DESC)
  if (query.sortBy) {
    if (query.sortDirection === 'ASC') {
      return ColumnSortDirections.ascend;
    }
    if (query.sortDirection === 'DESC') {
      return ColumnSortDirections.descend;
    }

    return null;
  }

  if (!query.ordinamenti) {
    return null;
  }

  return query.ordinamenti[0].startsWith('-')
    ? ColumnSortDirections.descend
    : ColumnSortDirections.ascend;
}

/**
 * Imposta l'ordinamento delle colonne in base a quanto indicato nella query.
 */
function setSorter<T, Q extends PaginationUrlQuery>(
  columns: ColumnsType<T>,
  query: Q,
) {
  // Rimuovo tutti i sorting e se necessario, imposto quelli richiesti dalla query
  const unsorted = resetSortOrder(columns);

  // Se non è presente un ordinamento, non faccio nulla
  if (!query.ordinamenti && !query.sortBy) {
    return unsorted;
  }

  const index = unsorted.findIndex(
    (column: ColumnType<T>) =>
      (column.sortDataIndex ?? column.dataIndex) === getOrderBy(query),
  );

  if (index < 0) {
    return unsorted;
  }

  unsorted[index] = {
    ...columns[index],
    // sorter: true,
    sortOrder:
      getOrderDirection(query) === ColumnSortDirections.ascend
        ? 'ascend'
        : 'descend',
  };
  return unsorted;
}

/**
 * Gestione dell'impostazione dei filtri automatici di default
 * in base alla query passata.
 * @param inputColumns Colonne di input della tabella
 * @param query Query utilizzata per filtrare i dati della tabella
 * @param filterMap mappatura dei campi della query rispetto ai filtri.
 * Per esempio la data di creazione (un filtro unico) contiene due valori (da-a); dovrà essere mappata come {createdAt: ['createdAtFrom', 'createdAtTo']}.
 * Oppure, se un dataIndex differisce dalla query, ex: { dataIndexLabelA: ['dataIndexLabelB']}
 * Se un campo mantiene la stessa chiave sia nella query che nel dataIndex, non è necessario indicarlo.
 * @returns l'oggetto colonne completo di paginazione da fornire alla tabella.
 */
export function getPaginationUrlColumns<T, Q extends PaginationUrlQuery>(
  columns: ColumnsType<T>,
  query: Q,
  filterMap?: FilterMap,
) {
  const sortedColumns = setSorter(columns, query);
  const filteredColumns = setFilters(sortedColumns, query, filterMap);
  return filteredColumns;
}
